import React, { useCallback, useState } from 'react';
import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { IOrderDDLRes, ISearchOrderReq } from '@/interfaces/order';
import { orderDDL } from '@/adapters/order';

interface IProps {
  value?: number;
  order?: IOrderDDLRes;
  size?: SizeType;
  isAll?: boolean;
  handleSelectData: (order: IOrderDDLRes) => void;
  readonly?: boolean;
  customClass?: string;
  allowClear?: boolean;
}

const _criteria: Partial<ISearchOrderReq> = {
  search: '',
};

const OrderSelector = ({
  value,
  size = 'large',
  handleSelectData,
  isAll = false,
  readonly = false,
  order,
  customClass,
  allowClear = true,
}: IProps) => {
  const [criteria, setCriteria] = useState<Partial<ISearchOrderReq>>({
    ..._criteria,
  });

  const { data: orders, isLoading } = useQuery<
    AxiosResponse<IOrderDDLRes[]>,
    Error
  >({
    queryKey: ['search-order=ddl', criteria],
    queryFn: async () => await orderDDL(criteria),
  });

  let groupOptions =
    orders?.data?.map(g => {
      return {
        value: g.id,
        label: `${g.code}`,
      };
    }) || [];

  const allOptions = [{ value: '', label: 'ทั้งหมด' }, ...groupOptions];

  const debouncedSearch = useCallback(
    debounce(value => {
      setCriteria({
        ...criteria,
        search: value,
      });
    }, 300),
    [],
  );

  const onChange = (value: number) => {
    const orderInfo = orders?.data?.find(d => d.id == value);
    if (orderInfo) handleSelectData(orderInfo);
  };

  const initOptions = {
    value: order?.id || 0,
    label: `${order?.code}`,
  };

  if (order) groupOptions = [...groupOptions, initOptions];
  return (
    <Select
      allowClear={allowClear}
      showSearch
      onSearch={debouncedSearch}
      loading={isLoading}
      value={value}
      className={twMerge('md:min-w-[150px] w-full', customClass)}
      size={size}
      placeholder="เลือกออเดอร์"
      onChange={value => onChange(value)}
      onClear={() => onChange(0)}
      options={isAll ? allOptions : [...groupOptions]}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      disabled={readonly}
    />
  );
};

export default OrderSelector;
